import { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useExport } from '../../hooks/useExport';

interface ExportOption {
  id: string;
  label: string;
  description: string;
  icon: React.ReactElement;
  action: () => void;
  keywords: string[];
  shortcut?: string;
}

const ExportModal = () => {
  const { state, dispatch } = useAppContext();
  const { status, exportHTML, exportMarkdown, exportPDF } = useExport();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Early return AFTER all hooks to avoid conditional hook calls
  if (!state.exportModal.isOpen) {
    return null;
  }

  // Enhanced export options with better icons and shortcuts
  const exportOptions: ExportOption[] = [
    {
      id: 'html',
      label: 'Export as HTML',
      description: 'GitHub-perfect rendering with embedded CSS',
      icon: (
        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0 0V3" />
        </svg>
      ),
      action: () => { exportHTML(); handleClose(); },
      keywords: ['html', 'web', 'github', 'style', 'css', 'browser'],
      shortcut: '⌘H'
    },
    {
      id: 'markdown',
      label: 'Download Markdown',
      description: 'Original markdown source file (.md)',
      icon: (
        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      action: () => { exportMarkdown(); handleClose(); },
      keywords: ['markdown', 'md', 'source', 'raw', 'download'],
      shortcut: '⌘M'
    },
    {
      id: 'pdf',
      label: 'Export as PDF',
      description: 'Print-optimized document via browser',
      icon: (
        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      action: () => { exportPDF(); handleClose(); },
      keywords: ['pdf', 'print', 'document', 'file'],
      shortcut: '⌘P'
    }
  ];

  // Filter options based on search query
  const filteredOptions = exportOptions.filter(option => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      option.label.toLowerCase().includes(query) ||
      option.description.toLowerCase().includes(query) ||
      option.keywords.some(keyword => keyword.includes(query))
    );
  });

  if (!state.exportModal.isOpen) {
    return null;
  }

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      dispatch({ type: 'TOGGLE_EXPORT_MODAL' });
      setSearchQuery('');
      setSelectedIndex(0);
      setIsAnimating(false);
    }, 200);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        handleClose();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (filteredOptions[selectedIndex]) {
          filteredOptions[selectedIndex].action();
        }
        break;
      default:
        break;
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setSelectedIndex(0); // Reset selection when search changes
  };

  // Focus search input when modal opens
  useEffect(() => {
    if (state.exportModal.isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [state.exportModal.isOpen]);

  return (
    <div className={`fixed inset-0 flex items-start justify-center p-4 pt-12 md:pt-20 transition-all duration-200 ${
      isAnimating ? 'opacity-0' : 'opacity-100'
    }`}
    style={{ 
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      zIndex: 'var(--cs-z-modal)'
    }}
    onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className={`cs-card max-w-lg w-full max-h-[80vh] overflow-hidden touch-manipulation cs-animate-scale-in ${
          isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        style={{ transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)' }}
      >
        {/* Enhanced Search Header */}
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Export Document</h3>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search export options..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full cs-input text-sm py-1.5"
              />
            </div>
            <button
              onClick={handleClose}
              className="cs-btn-ghost cs-btn-sm p-1.5"
              title="Close (Escape)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Enhanced Export Status */}
        {status.isExporting && (
          <div className="px-5 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
            <div className="flex items-center space-x-3">
              <div className="cs-spinner w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              <div className="flex-1">
                <div className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  Exporting...
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400">
                  {status.progress}% complete
                </div>
              </div>
            </div>
          </div>
        )}

        {status.error && (
          <div className="px-5 py-3 cs-toast-error border-b">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-red-700 dark:text-red-300">
                {status.error}
              </span>
            </div>
          </div>
        )}

        {status.success && (
          <div className="px-5 py-3 cs-toast-success border-b">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-green-700 dark:text-green-300">
                Export completed successfully!
              </span>
            </div>
          </div>
        )}

        {/* Enhanced Export Options */}
        <div className="max-h-64 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                No export options match "{searchQuery}"
              </p>
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <button
                key={option.id}
                onClick={option.action}
                disabled={status.isExporting}
                className={`w-full flex items-center space-x-4 px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation group ${
                  index === selectedIndex 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-r-4 border-blue-500' 
                    : ''
                }`}
              >
                <div className="flex-shrink-0 w-10 h-10 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center group-hover:bg-gray-100 dark:group-hover:bg-gray-600 transition-colors">
                  {option.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {option.label}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {option.description}
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0 text-xs text-gray-400">
                  {option.shortcut && (
                    <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                      {option.shortcut}
                    </kbd>
                  )}
                  {index === selectedIndex && (
                    <span className="text-blue-500">⏎</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Enhanced Footer */}
        <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-600 rounded text-xs">↑↓</kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-600 rounded text-xs">⏎</kbd>
                <span>Select</span>
              </div>
              <div className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-600 rounded text-xs">Esc</kbd>
                <span>Close</span>
              </div>
            </div>
            <div className="text-xs text-gray-400">
              Content Studio Export
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;